import React from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";

import { PROJECT_TRACKING } from "common/constants";
import { formatCurrency } from "common/utils/strings";

import "./__styles__/Summary.scss";

type Status = "under" | "close" | "over";

export interface SummaryProps {
  projectThresholdPercentage: number;
  projectTracking: string;
  cumulativeTrackingPeriod: Maybe<number | undefined>;
  improvementTotal: number;
  cumulativeImprovementProgress?: Maybe<number>;
  status?: Status;
}
const Summary = ({
  projectThresholdPercentage,
  projectTracking,
  cumulativeTrackingPeriod,
  improvementTotal,
  cumulativeImprovementProgress,
  status,
}: SummaryProps) => {
  const { t } = useTranslation();

  const threshold = (projectThresholdPercentage ?? 0) * 100;
  const progress = (cumulativeImprovementProgress ?? 0) * 100;
  const trackByProject = projectTracking === PROJECT_TRACKING.PER_PROJECT;
  const percentageSummaryText = trackByProject
    ? t("property-sisd-per-project-percentage")
    : t("property-sisd-cumulative-percentage");
  const costSummaryText = trackByProject
    ? t("property-sisd-per-project-costs")
    : t("property-sisd-cumulative-costs");

  // Cap to 100% to avoid stretching the progress bar
  const progressFill = Math.min(100, progress);

  return (
    <table styleName="summary" data-testid="sisd-summary">
      <tbody>
        <tr>
          <td colSpan={3}>
            <div styleName="progress-bar">
              <div
                styleName={cx("fill", status)}
                style={{ width: `${progressFill}%` }}
              />
              <div styleName="threshold" style={{ left: `${threshold}%` }} />
            </div>
          </td>
        </tr>
        <tr>
          <td>{percentageSummaryText}</td>
          <td></td>
          <td styleName={cx("total-costs", status)}>{progress.toFixed(2)}%</td>
        </tr>
        <tr>
          <td>{costSummaryText}</td>
          <td></td>
          <td styleName={cx("total-costs", status)}>
            {formatCurrency(improvementTotal)}
          </td>
        </tr>
        {projectTracking === PROJECT_TRACKING.CUMULATIVE &&
          cumulativeTrackingPeriod && (
            <tr>
              <td>{t("property-sisd-tracking-period")}</td>
              <td></td>
              <td>{t("common-year", { count: cumulativeTrackingPeriod })}</td>
            </tr>
          )}
      </tbody>
    </table>
  );
};

export default Summary;
